import { DbEnum } from "../../../generated/prisma-enums";
import { DEFAULT_DEPLOYMENT_TYPE, INTEGRATION_DEPLOYMENT_MAPPINGS } from "./agent-prompt.constants";

/** Tab context key used for natural-speech (voice) prompt content. */
export const PHONE_CALL_DEPLOYMENT_TYPE = "PHONE_CALL" as const;

/**
 * Minimal deployment shape needed to resolve prompt tab context keys.
 * `deploymentSettings` is the raw settings JSON as stored on the deployment
 * (`Deployment.settings`) / returned by the deployments API.
 */
export interface DeploymentContextSource {
  readonly type: string;
  readonly deploymentSettings?: unknown;
}

function hasVoiceEnabled(deploymentSettings: unknown): boolean {
  if (typeof deploymentSettings !== "object" || deploymentSettings === null) return false;
  const voiceSettings = (deploymentSettings as { voiceSettings?: unknown }).voiceSettings;
  if (typeof voiceSettings !== "object" || voiceSettings === null) return false;
  return (voiceSettings as { enabled?: unknown }).enabled === true;
}

/**
 * Resolve which prompt-editor tab context keys a deployment contributes.
 *
 * Most deployments contribute exactly one key, straight from
 * `INTEGRATION_DEPLOYMENT_MAPPINGS`. EMBED is the exception and the reason this
 * function exists rather than a plain map lookup (NEX-2818):
 *
 * An EMBED deployment normally shares the NEXUS tab's content. But when its
 * voice-call feature is switched on (`settings.voiceSettings.enabled`), the
 * runtime renders that call's system prompt with PHONE_CALL formatting — see
 * `build-voice-system-prompt.ts`, which the embed web-call path drives with
 * `TWILIO_VOICE`. Without a PHONE_CALL tab there is nowhere to author the voice
 * behaviour, so an embed-with-voice deployment contributes PHONE_CALL too.
 *
 * Unmapped deployment types contribute nothing. `NEXUS` is returned as-is when it
 * is the mapped key — callers that render a channel tab bar filter it out, since
 * NEXUS is the always-present default tab.
 */
export function resolveDeploymentContextKeys(deployment: DeploymentContextSource): string[] {
  const keys: string[] = [];

  const mapped = INTEGRATION_DEPLOYMENT_MAPPINGS[deployment.type as DbEnum.DeploymentType];
  if (mapped !== undefined) keys.push(mapped);

  if (
    deployment.type === DbEnum.DeploymentType.EMBED &&
    hasVoiceEnabled(deployment.deploymentSettings)
  ) {
    keys.push(PHONE_CALL_DEPLOYMENT_TYPE);
  }

  return keys;
}

/**
 * Channel tab keys a set of deployments contributes, deduplicated and with the
 * always-present default (`NEXUS`) removed — i.e. exactly the extra tabs the
 * channel tab bar should offer.
 */
export function resolveChannelTabKeys(deployments: readonly DeploymentContextSource[]): string[] {
  const keys = new Set<string>();
  for (const deployment of deployments) {
    for (const key of resolveDeploymentContextKeys(deployment)) {
      if (key !== DEFAULT_DEPLOYMENT_TYPE) keys.add(key);
    }
  }
  return [...keys];
}
